import { ref } from 'vue';
import callDevicesAPI from 'dashboard/api/callDevices';

const POLL_INTERVAL_MS = 2000;

/**
 * Drives the WaCalls QR pairing/reconnect flow for a single device:
 * optionally re-emits a fresh QR (repair), then polls the device until it
 * reports `paired`. Exposes `qr` (string to render) and `paired` (boolean).
 *
 * The caller owns the canvas rendering (watch `qr`) and the surrounding UI.
 */
export function useDevicePairing() {
  const qr = ref('');
  const paired = ref(false);
  let timer = null;

  const stop = () => {
    if (timer) {
      clearInterval(timer);
      timer = null;
    }
  };

  const start = async (deviceId, { repair = false } = {}) => {
    stop();
    qr.value = '';
    paired.value = false;

    if (repair) {
      try {
        // A previously-paired device keeps its stale WhatsApp credentials, so
        // WaCalls stays "connecting" and never emits a QR. Log it out first,
        // then pair() drops the stale session and yields a fresh QR.
        await callDevicesAPI.logout(deviceId);
        await callDevicesAPI.pair(deviceId);
      } catch {
        // non-fatal — polling show() may still surface a QR
      }
    }

    timer = setInterval(async () => {
      try {
        const { data } = await callDevicesAPI.show(deviceId);
        if (data.paired) {
          paired.value = true;
          stop();
          return;
        }
        if (data.qr) qr.value = data.qr;
      } catch {
        // transient error — keep polling
      }
    }, POLL_INTERVAL_MS);
  };

  return { qr, paired, start, stop };
}
